/**
 * SessionExpiredModal — shown when the shared token-refresh layer gives up.
 * Confirming clears the session and returns the visitor to the login screen.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { BrandLogo } from '../ui/BrandLogo';
import '../layout/authFlow.css';

export function SessionExpiredModal() {
  const { sessionExpiredVisible, confirmSessionExpiredAndLogout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sessionExpiredVisible) {
      dialogRef.current?.focus();
    }
  }, [sessionExpiredVisible]);

  if (!sessionExpiredVisible) {
    return null;
  }

  const confirm = async () => {
    await confirmSessionExpiredAndLogout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="auth-flow__modal-overlay" role="presentation">
      <div
        ref={dialogRef}
        className="auth-flow__modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-expired-title"
        aria-describedby="session-expired-message"
        tabIndex={-1}
      >
        <BrandLogo compact className="auth-flow__modal-mark" />
        <h2 id="session-expired-title">{t('auth.sessionExpired.title')}</h2>
        <p id="session-expired-message">{t('auth.sessionExpired.message')}</p>
        <div className="auth-flow__modal-actions">
          <button className="auth-flow__primary" type="button" onClick={confirm} autoFocus>
            {t('auth.sessionExpired.loginAgain')}
          </button>
        </div>
      </div>
    </div>
  );
}
